import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GlobeAltIcon,
  PencilSquareIcon,
  BookOpenIcon,
  PaintBrushIcon,
  ArrowUturnLeftIcon
} from '@heroicons/react/24/outline';
import {
  ProjectSettingType,
  IProjectSetting,
  ChatContextType
} from '@packages/global';
import SettingChatPanel from './SettingChatPanel';

// 自定义 Save 图标 (lucide save)
function SaveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
      <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" />
      <path d="M7 3v4a1 1 0 0 0 1 1h7" />
    </svg>
  );
}

// ProjectSettingType 到 ChatContextType 的映射
const settingToChatContext: Record<ProjectSettingType, ChatContextType> = {
  [ProjectSettingType.STORY_SYNOPSIS]: ChatContextType.STORY_SYNOPSIS,
  [ProjectSettingType.WORLDVIEW]: ChatContextType.WORLDVIEW,
  [ProjectSettingType.WRITING_STYLE]: ChatContextType.WRITING_STYLE,
  [ProjectSettingType.ART_STYLE]: ChatContextType.ART_STYLE
};

interface SettingEditorProps {
  projectId: string;
  settingType: ProjectSettingType;
  setting?: IProjectSetting | null;
  loading?: boolean;
  onSave: (content: string) => Promise<void>;
}

const settingIcons: Record<ProjectSettingType, React.ComponentType<{ className?: string }>> = {
  [ProjectSettingType.WORLDVIEW]: GlobeAltIcon,
  [ProjectSettingType.WRITING_STYLE]: PencilSquareIcon,
  [ProjectSettingType.ART_STYLE]: PaintBrushIcon,
  [ProjectSettingType.STORY_SYNOPSIS]: BookOpenIcon
};

export default function SettingEditor({
  projectId,
  settingType,
  setting,
  loading,
  onSave
}: SettingEditorProps) {
  const { t } = useTranslation(['projectSettings', 'common']);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const previousContentRef = useRef<string | null>(null);

  // 从 setting 初始化内容
  useEffect(() => {
    const initialContent = setting?.content || '';
    setContent(initialContent);
    previousContentRef.current = null; // 重置撤销状态
  }, [setting]);

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
  };

  const handleApplyContent = async (aiContent: string) => {
    // 保存当前内容用于撤销
    previousContentRef.current = content;
    setContent(aiContent);
    // 自动保存
    setSaving(true);
    try {
      await onSave(aiContent);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!content.trim()) return;
    // 保存当前内容用于撤销
    previousContentRef.current = content;
    setSaving(true);
    try {
      await onSave(content);
    } finally {
      setSaving(false);
    }
  };

  const handleUndo = async () => {
    if (previousContentRef.current === null) return;
    const previousContent = previousContentRef.current;
    previousContentRef.current = content; // 允许再次撤销回来
    setContent(previousContent);
    // 自动保存撤销后的内容
    setSaving(true);
    try {
      await onSave(previousContent);
    } finally {
      setSaving(false);
    }
  };

  const canUndo = previousContentRef.current !== null;

  const label = t(`setting.label.${settingType}`);
  const Icon = settingIcons[settingType];
  const description = t(`setting.description.${settingType}`);
  const chatContextType = settingToChatContext[settingType];

  if (loading) {
    return (
      <div className="h-full flex flex-col bg-bg-surface rounded-xl border border-border-subtle p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-5 w-5 bg-bg-elevated animate-pulse rounded" />
          <div className="h-5 w-24 bg-bg-elevated animate-pulse rounded" />
        </div>
        <div className="h-4 w-full bg-bg-elevated animate-pulse rounded mb-4" />
        <div className="flex-1 bg-bg-elevated animate-pulse rounded" />
      </div>
    );
  }

  return (
    <div className="h-full flex gap-4 min-h-0">
      {/* 左侧编辑区 */}
      <div className="flex-1 flex flex-col bg-bg-surface rounded-xl border border-border-subtle p-5 min-h-0">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4 flex-shrink-0">
          <Icon className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold text-gray-50">{label}</h2>
        </div>

        {/* Description */}
        <p className="text-[13px] text-gray-400 mb-4 flex-shrink-0">{description}</p>

        {/* Text Area */}
        <div className="flex-1 min-h-0 bg-bg-page rounded-lg border border-border-subtle overflow-hidden">
          <textarea
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder={t('setting.placeholder')}
            className="w-full h-full p-4 bg-transparent text-gray-200 placeholder-gray-500 resize-none focus:outline-none text-sm leading-[1.7]"
          />
        </div>

        {/* Save Footer */}
        <div className="flex justify-center gap-3 pt-4 flex-shrink-0">
          <button
            onClick={handleUndo}
            disabled={saving || !canUndo}
            className="flex items-center gap-2 px-4 py-2.5 bg-bg-elevated hover:bg-border-subtle text-gray-300 hover:text-gray-50 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-border-default"
            title={t('setting.undoTitle')}
          >
            <ArrowUturnLeftIcon className="w-4 h-4" />
            {t('setting.undo')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !content.trim()}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SaveIcon className="w-4 h-4" />
            {saving ? t('setting.savingBtn') : t('setting.saveBtn')}
          </button>
        </div>
      </div>

      {/* 右侧 AI 聊天面板 */}
      <div className="w-[400px] flex-shrink-0 min-h-0">
        <SettingChatPanel
          projectId={projectId}
          contextType={chatContextType}
          currentContent={content}
          onApply={handleApplyContent}
        />
      </div>
    </div>
  );
}
